// Renders an MJPEG camera stream by subscribing to the module-level frame hub.
// The registry maintains ONE hidden <img> per URL — so any number of instances
// showing the same camera URL share a single HTTP connection to the camera.
//
// Why we don't put `style={{ display: 'none' }}` on the <img> in JSX:
//     React re-applies JSX-declared styles on every re-render. If we set
//     `imgRef.current.style.display = ''` directly but JSX still says
//     `style={{ display: 'none' }}`, the next re-render (triggered by
//     setStatus) overrides our DOM change and hides the frame.
//     Solution: let React control display exclusively via the `showFrame`
//     state variable; only the `src` is updated directly (no React interference
//     since `src` is intentionally absent from JSX).

import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { mjpegRegistry, MjpegStatus } from '../utils/mjpegStreamRegistry';

interface MjpegLivePreviewProps {
  url: string;
  className?: string;
  /** Compact mode: smaller overlays for tight spaces (e.g. circle columns) */
  compact?: boolean;
}

export const MjpegLivePreview: React.FC<MjpegLivePreviewProps> = ({
  url,
  className = '',
  compact = false,
}) => {
  const [status, setStatus] = useState<MjpegStatus>('connecting');
  // `showFrame` lets React control the img's display style — see note above
  const [showFrame, setShowFrame] = useState(false);
  const [hovered, setHovered] = useState(false);
  const idRef       = useRef<string>(`mjpeg-${Math.random().toString(36).slice(2)}`);
  const imgRef      = useRef<HTMLImageElement | null>(null);
  // Ref so the registry callback (closure) can check current state without stale capture
  const hasFrameRef = useRef(false);

  useEffect(() => {
    if (!url) return;

    // Reset on URL change
    setStatus('connecting');
    setShowFrame(false);
    hasFrameRef.current = false;
    if (imgRef.current) imgRef.current.src = '';

    return mjpegRegistry.subscribe(url, idRef.current, (dataUrl, s) => {
      // Status change → triggers React re-render (infrequent: connecting→live, live→error)
      setStatus(s);

      if (dataUrl && imgRef.current) {
        // Update src directly — NOT via JSX so React never overrides it
        imgRef.current.src = dataUrl;
        // Show the img via state (only triggers one extra re-render on first frame)
        if (!hasFrameRef.current) {
          hasFrameRef.current = true;
          setShowFrame(true);
        }
      } else if (!dataUrl && hasFrameRef.current) {
        // Stream reset / error — hide img
        hasFrameRef.current = false;
        setShowFrame(false);
        if (imgRef.current) imgRef.current.src = '';
      }
    });
  }, [url]);

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    mjpegRegistry.refresh(url);
  };

  const sm = compact;

  return (
    <div
      className={`relative overflow-hidden bg-black ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/*
        `src` intentionally absent from JSX — updated directly via imgRef.
        `style` is React-controlled via showFrame:
          • false → display:none  (no frame yet)
          • true  → no style prop  (React removes display, img visible)
        This prevents React's reconciler from hiding frames we already painted.
      */}
      <img
        ref={imgRef}
        alt="MJPEG stream"
        className="w-full h-full object-cover block"
        style={showFrame ? undefined : { display: 'none' }}
      />

      {/* Connecting overlay */}
      {status === 'connecting' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-gray-900/80 pointer-events-none">
          <RefreshCw className={`${sm ? 'w-3 h-3' : 'w-4 h-4'} text-gray-400 animate-spin`} />
          {!sm && <span className="text-[9px] text-gray-400">Conectando...</span>}
        </div>
      )}

      {/* Error overlay */}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-gray-900/90">
          <WifiOff className={`${sm ? 'w-3 h-3' : 'w-4 h-4'} text-gray-500`} />
          {!sm && <span className="text-[9px] text-gray-400">Sem conexão</span>}
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white text-[9px] font-medium transition-colors"
          >
            <RefreshCw className="w-2.5 h-2.5" />
            {!sm && 'Reconectar'}
          </button>
        </div>
      )}

      {/* AO VIVO badge */}
      {status === 'live' && (
        <div
          className={`absolute pointer-events-none flex items-center gap-0.5 bg-red-600/90 backdrop-blur-sm text-white font-semibold rounded-full
            ${sm ? 'top-1 left-1 px-1 py-0.5 text-[7px]' : 'top-1.5 left-1.5 px-1.5 py-0.5 text-[9px]'}`}
        >
          <span className={`${sm ? 'w-1 h-1' : 'w-1.5 h-1.5'} bg-white rounded-full animate-pulse flex-shrink-0`} />
          AO VIVO
        </div>
      )}

      {/* Hover refresh button */}
      {status !== 'error' && hovered && (
        <button
          type="button"
          onClick={handleRefresh}
          title="Reconectar câmera"
          className={`absolute flex items-center justify-center rounded bg-black/50 hover:bg-black/70 text-white transition-colors
            ${sm ? 'top-1 right-1 w-4 h-4' : 'top-1.5 right-1.5 w-5 h-5'}`}
        >
          <RefreshCw className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
};
